/**
 * Utility functions to strip URLs and clean up display text for client views.
 */

export function cleanTitle(title: string | any[] | Record<string, any> | null | undefined): string {
  if (!title) return "";
  
  let strTitle = "";
  if (typeof title === "string") {
    strTitle = title;
  } else if (Array.isArray(title)) {
    strTitle = title.filter(Boolean).map(String).join(" ");
  } else if (typeof title === "object") {
    strTitle = String(title.title || title.name || title.label || "");
  } else {
    strTitle = String(title);
  }

  if (!strTitle.trim()) return "";

  // 1. Remove standard http/https/ftp URLs (onion, urlscan, or clearweb)
  let cleaned = strTitle.replace(/https?:\/\/[^\s]+/gi, "");
  
  // 2. Remove standalone onion domains (e.g. abc.onion or abc.onion.pet) and onion hashes
  cleaned = cleaned.replace(/\b[a-z2-7]{16,56}\.onion(?:\.[a-z]+)?\b/gi, "");
  cleaned = cleaned.replace(/\b[a-z2-7]{16,56}\b/gi, (match) => {
    if (/^[a-z2-7]{16}$|^[a-z2-7]{56}$/i.test(match)) return "";
    return match;
  });
  cleaned = cleaned.replace(/\b(?:https?:\/\/)?(?:www\.)?urlscan\.io[^\s]*\b/gi, "");
  
  // 3. Trim whitespace and clean up trailing separators (hyphens, slashes, pipes)
  cleaned = cleaned.trim().replace(/^[\s\-|/]+|[\s\-|/]+$/g, "");

  // 4. Reject access banners or DDoS / placeholder text
  const lower = cleaned.toLowerCase();
  if (
    lower.includes("this page is currently private") ||
    lower.includes("only you have access to it") ||
    lower.includes("just a moment") ||
    lower.includes("attention required") ||
    lower.includes("ddos-guard") ||
    lower.includes("challenge validation")
  ) {
    return "";
  }
  
  return cleaned || "Active Leak Target";
}

export function isUrlscanLeak(leak: { forum?: string | null; domain?: string | null; source_url?: string | null; crawler_id?: string | null } | null | undefined): boolean {
  if (!leak) return false;
  const f = (leak.forum || "").toLowerCase();
  const d = (leak.domain || "").toLowerCase();
  const s = (leak.source_url || "").toLowerCase();
  const c = (leak.crawler_id || "").toLowerCase();
  return f.includes("urlscan") || d.includes("urlscan") || s.includes("urlscan") || c.includes("urlscan");
}

export function isOnionLeak(leak: { forum?: string | null; domain?: string | null; source_url?: string | null } | null | undefined): boolean {
  if (!leak) return false;
  const f = (leak.forum || "").toLowerCase();
  const d = (leak.domain || "").toLowerCase();
  const s = (leak.source_url || "").toLowerCase();
  return f.includes(".onion") || d.includes(".onion") || s.includes(".onion") || /^[a-z2-7]{16,56}/i.test(f) || /^[a-z2-7]{16,56}/i.test(d);
}

export function getDisplayForum(forum: string | null | undefined, sectors: string[] | null | undefined): string {
  if (!forum) return "Sector Intelligence";
  
  const fLower = forum.toLowerCase();
  const isOnion = fLower.includes(".onion") || /^[a-z2-7]{16,56}$/i.test(fLower);
  const isUrlScan = fLower.includes("urlscan");
  
  if (isOnion || isUrlScan) {
    return sectors && sectors.length > 0 ? sectors[0] : "Technology";
  }
  return forum;
}

export function stripOnionUrls(text: string | any[] | null | undefined): string {
  if (!text) return "";
  let cleaned = typeof text === "string" ? text : (Array.isArray(text) ? text.join(" ") : String(text));
  cleaned = cleaned.replace(/https?:\/\/[a-z2-7]{16,56}\.onion[^\s]*/gi, "[redacted]");
  cleaned = cleaned.replace(/\b[a-z2-7]{16,56}\.onion(?:\.[a-z]+)?\b/gi, "[redacted]");
  cleaned = cleaned.replace(/https?:\/\/(?:www\.)?urlscan\.io[^\s]*/gi, "[redacted]");
  cleaned = cleaned.replace(/\b(?:www\.)?urlscan\.io[^\s]*/gi, "[redacted]");
  return cleaned;
}
